// apps/web/src/components/parent/TemplateConfig.tsx
import { useState, useEffect, useCallback } from 'react';
import type { ChecklistTemplate, ChecklistItemType, VerificationType } from '@homework-app/shared';

interface Props {
  studentId: string;
  apiService: {
    getTemplates(): Promise<ChecklistTemplate[]>;
    createTemplate(data: Partial<ChecklistTemplate> & { student_id: string }): Promise<ChecklistTemplate>;
    updateTemplate(id: string, data: Partial<ChecklistTemplate>): Promise<ChecklistTemplate>;
    deleteTemplate(id: string): Promise<void>;
    seedTemplates(studentId: string): Promise<ChecklistTemplate[]>;
  };
}

const ITEM_TYPE_OPTIONS: Array<{ value: ChecklistItemType; label: string }> = [
  { value: 'homework', label: 'Homework' },
  { value: 'backpack', label: 'Backpack' },
  { value: 'trumpet', label: 'Trumpet' },
  { value: 'reading', label: 'Reading' },
  { value: 'custom', label: 'Custom' }
];

const VERIFICATION_TYPE_OPTIONS: Array<{ value: VerificationType; label: string }> = [
  { value: 'ai_photo', label: 'AI Photo Verification' },
  { value: 'timer', label: 'Timer' },
  { value: 'honor', label: 'Honor System' },
  { value: 'parent_only', label: 'Parent Review Only' }
];

const DAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' }
];

interface NewTemplate {
  title: string;
  description: string;
  item_type: ChecklistItemType;
  verification_type: VerificationType;
  day_of_week: string;
  config: Record<string, unknown>;
}

const EMPTY_TEMPLATE: NewTemplate = {
  title: '',
  description: '',
  item_type: 'custom',
  verification_type: 'parent_only',
  day_of_week: 'mon,tue,wed,thu,fri',
  config: {}
};

export function TemplateConfig({ studentId, apiService }: Props) {
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTemplate, setNewTemplate] = useState<NewTemplate>({ ...EMPTY_TEMPLATE });
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  }, [apiService]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSeedDefaults = async () => {
    try {
      setSaving(true);
      await apiService.seedTemplates(studentId);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to seed templates:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newTemplate.title.trim()) return;

    try {
      setSaving(true);
      await apiService.createTemplate({
        student_id: studentId,
        ...newTemplate
      });
      setNewTemplate({ ...EMPTY_TEMPLATE });
      setShowAdd(false);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to create template:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (template: ChecklistTemplate) => {
    try {
      await apiService.updateTemplate(template.id, { active: !template.active });
      await loadTemplates();
    } catch (err) {
      console.error('Failed to toggle template:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteTemplate(id);
      await loadTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const toggleDay = (day: string) => {
    const days = newTemplate.day_of_week.split(',').filter(Boolean);
    const updated = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day];
    setNewTemplate({ ...newTemplate, day_of_week: updated.join(',') });
  };

  if (loading) return <div>Loading templates...</div>;

  return (
    <div className="template-config">
      <h2>Checklist Templates</h2>

      {templates.length === 0 && (
        <div className="template-empty">
          <p>No templates configured yet.</p>
          <button onClick={handleSeedDefaults} disabled={saving}>
            {saving ? 'Setting up...' : 'Set Up Default Templates'}
          </button>
        </div>
      )}

      <div className="template-list">
        {templates.map(tmpl => (
          <div key={tmpl.id} className={`template-card ${tmpl.active ? '' : 'inactive'}`}>
            <div className="template-card-header">
              <h3>{tmpl.title}</h3>
              <div className="template-card-actions">
                <button onClick={() => handleToggle(tmpl)} className="btn-toggle">
                  {tmpl.active ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => handleDelete(tmpl.id)} className="btn-delete">
                  Remove
                </button>
              </div>
            </div>
            {tmpl.description && <p className="template-description">{tmpl.description}</p>}
            <div className="template-meta">
              <span className="template-type">{tmpl.item_type}</span>
              <span className="template-verification">{tmpl.verification_type}</span>
              <span className="template-days">{tmpl.day_of_week}</span>
            </div>
          </div>
        ))}
      </div>

      {showAdd ? (
        <div className="template-add-form">
          <h3>Add Template</h3>

          <label>
            Title
            <input
              type="text"
              value={newTemplate.title}
              onChange={e => setNewTemplate({ ...newTemplate, title: e.target.value })}
              placeholder="e.g., Clean Room"
            />
          </label>

          <label>
            Description
            <input
              type="text"
              value={newTemplate.description}
              onChange={e => setNewTemplate({ ...newTemplate, description: e.target.value })}
              placeholder="Optional description"
            />
          </label>

          <label>
            Type
            <select
              value={newTemplate.item_type}
              onChange={e => setNewTemplate({ ...newTemplate, item_type: e.target.value as ChecklistItemType })}
            >
              {ITEM_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          <label>
            Verification
            <select
              value={newTemplate.verification_type}
              onChange={e => setNewTemplate({ ...newTemplate, verification_type: e.target.value as VerificationType })}
            >
              {VERIFICATION_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          <div className="day-picker">
            <label>Days</label>
            <div className="day-buttons">
              {DAY_OPTIONS.map(day => (
                <button
                  key={day.value}
                  className={`day-btn ${newTemplate.day_of_week.includes(day.value) ? 'active' : ''}`}
                  onClick={() => toggleDay(day.value)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          {newTemplate.verification_type === 'timer' && (
            <label>
              Timer (minutes)
              <input
                type="number"
                value={(newTemplate.config as Record<string, number>).timer_minutes || 15}
                onChange={e => setNewTemplate({
                  ...newTemplate,
                  config: { ...newTemplate.config, timer_minutes: parseInt(e.target.value) || 15 }
                })}
                min={1}
                max={120}
              />
            </label>
          )}

          <div className="form-actions">
            <button onClick={handleAdd} disabled={saving || !newTemplate.title.trim()}>
              {saving ? 'Saving...' : 'Add Template'}
            </button>
            <button onClick={() => setShowAdd(false)} className="btn-cancel">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="btn-add-template">
          + Add Template
        </button>
      )}
    </div>
  );
}
